import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { clearAuthStorage, logoutService } from "../services/authService";

const AuthContext = createContext({});

// Storage keys
// The user's identity token (from the initial Identity/Auth login) lives under
// a dedicated key so the generic-user DMS token swapped in after login doesn't
// clobber it. "vizy-bility-token" is intentionally NOT used here — that key
// holds the generic DMS token consumed by DealerWebService call sites.
const TOKEN_KEY         = "vizy-identity-token";
const REFRESH_TOKEN_KEY = "vizy-identity-refresh-token";
const USER_KEY          = "client_info";
const KEEP_SIGNED_IN_KEY = "vizy-keep-signed-in";

/**
 * Returns true if the JWT token is present and not expired.
 */
const isValidToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return !!(decoded && decoded.exp && Date.now() < decoded.exp * 1000);
  } catch {
    return false;
  }
};

/**
 * Decodes a JWT and normalises Microsoft-style long claim URIs to short keys.
 */
const decodeUserFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);

    const rawEmail =
      decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ||
      decoded.email;
    const email = Array.isArray(rawEmail) ? rawEmail[0] : rawEmail;

    const rawRole =
      decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
      decoded.role ||
      decoded.appType;
    const normalizedRole = Array.isArray(rawRole)
      ? rawRole
      : rawRole
        ? [rawRole]
        : [];

    return {
      ...decoded,
      email,
      role: normalizedRole,
      userId:    decoded["UserId"] || decoded.sub,
      name:      decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
      firstName: decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"],
      lastName:  decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"],
      userType:  decoded["UserType"],
    };
  } catch (err) {
    console.error("Error decoding token:", err);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser]                   = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading]             = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem(KEEP_SIGNED_IN_KEY);
    clearAuthStorage();
  }, []);

  /**
   * On mount: restore auth state from localStorage if token is still valid.
   */
  const initializeAuth = useCallback(() => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token && isValidToken(token)) {
        const decoded = decodeUserFromToken(token);
        if (decoded) {
          localStorage.setItem(USER_KEY, JSON.stringify(decoded));
          setUser(decoded);
          setIsAuthenticated(true);
        } else {
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Accepts the raw JWT (and optional refresh token) returned by loginService.
   * Validates, decodes, persists, and updates state.
   * Returns { success, user } or { success: false, error }.
   */
  const login = useCallback((token, refreshToken = null, rememberMe = false) => {
    try {
      if (!token || !isValidToken(token)) throw new Error("Invalid or expired token");

      const decoded = decodeUserFromToken(token);
      if (!decoded) throw new Error("Failed to decode token");

      localStorage.setItem(TOKEN_KEY, token);
      // localStorage.setItem("azuretoken", token);   // backward-compat for legacy components
      localStorage.setItem(USER_KEY, JSON.stringify(decoded));
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }

      if (rememberMe) {
        localStorage.setItem(KEEP_SIGNED_IN_KEY, "1");
      } else {
        localStorage.removeItem(KEEP_SIGNED_IN_KEY);
      }

      setUser(decoded);
      setIsAuthenticated(true);

      return { success: true, user: decoded };
    } catch (err) {
      console.error("Login error:", err);
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(async () => {
    await logoutService();
    clearAuth();
    navigate("/");
  }, [clearAuth, navigate]);

  const isAdmin = (() => {
    if (!user) return false;
    const roles = Array.isArray(user.role) ? user.role : user.role ? [user.role] : [];
    return roles.includes("Admin");
  })();

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
